import { ArrowDownLeft, ArrowUpRight, ChevronLeft, ChevronRight, FileText, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TableCell, TableHead } from "@/components/ui/table";
import { Tooltip } from "@/components/ui/misc";
import { CALLS_PAGE_SIZE } from "@/lib/queries";
import type { Cdr } from "@/lib/types";

/**
 * The parts two call tables share.
 *
 * There are two of them because there are two questions: `routes/calls.tsx` is the floor's
 * report — every leg the switch finished, for a supervisor — and `routes/history.tsx` is one
 * person's own calls. The audiences want different columns, different filters and different
 * words, so they are two screens rather than one screen with a toggle. What they cannot
 * usefully differ on is how a page is counted, how a page is turned, and which way an arrow
 * points, and those live here so the two never drift into disagreeing about a number.
 */

/**
 * "1–50 of 340", or "50 shown" when the server did not send a total.
 *
 * The two must not look alike: a missing total is not a total of nothing.
 */
export function CallRangeLabel({
  from, count, total,
}: {
  from: number;
  count: number;
  total: number | null;
}) {
  if (count === 0) return null;
  if (total === null) return <>{count} shown</>;
  return (
    <>
      {from + 1}–{from + count} of {total.toLocaleString()}
    </>
  );
}

export function CallPager({
  page, count, total, onChange, busy, pageSize = CALLS_PAGE_SIZE,
}: {
  page: number;
  count: number;
  total: number | null;
  onChange: (page: number) => void;
  busy: boolean;
  /** Rows per page, for a table of something other than calls (a campaign's leads). */
  pageSize?: number;
}) {
  // A short page is the end of the list whatever the total says — the same rule `paging.ts`
  // applies to the infinite lists, for the same reason.
  const last = count < pageSize || (total !== null && (page + 1) * pageSize >= total);
  if (page === 0 && last) return null;

  const pages = total === null ? null : Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2">
      <span className="text-xs text-text-muted">
        Page {page + 1}
        {pages !== null && ` of ${pages}`}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page === 0 || busy}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeft />
          Previous
        </Button>
        <Button variant="outline" size="sm" disabled={last || busy} onClick={() => onChange(page + 1)}>
          Next
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

/**
 * The switch's own reading of a channel: `inbound` arrived at the switch, `outbound` the switch
 * created. That is the right reading for the floor's report, which is a record of what the
 * switch did — and the wrong one for somebody's own history, where the question is who dialled
 * whom. `MyDirectionIcon` below answers that one.
 */
export function DirectionIcon({ direction }: { direction: Cdr["direction"] }) {
  if (direction === "inbound") {
    return (
      <Tooltip content="Inbound">
        <ArrowDownLeft className="size-3.5 text-accent-11" aria-label="Inbound" />
      </Tooltip>
    );
  }
  if (direction === "outbound") {
    return (
      <Tooltip content="Outbound">
        <ArrowUpRight className="size-3.5 text-text-subtle" aria-label="Outbound" />
      </Tooltip>
    );
  }
  return <span className="inline-block size-3.5" aria-hidden />;
}

/**
 * Placed by the reader, or rung for them. `null` renders nothing rather than an arrow pointing
 * somewhere — see `myDirection` for when that happens and why a guess would be worse.
 */
export function MyDirectionIcon({ direction }: { direction: "placed" | "received" | null }) {
  if (direction === "placed") {
    return (
      <Tooltip content="You placed this call">
        <ArrowUpRight className="size-3.5 text-text-subtle" aria-label="Placed" />
      </Tooltip>
    );
  }
  if (direction === "received") {
    return (
      <Tooltip content="You were rung for this call">
        <ArrowDownLeft className="size-3.5 text-accent-11" aria-label="Received" />
      </Tooltip>
    );
  }
  return <span className="inline-block size-3.5" aria-hidden />;
}

/**
 * The media column, header and cell, for both call tables: what there is to open this call for.
 *
 * It exists because the audio was reaching the bucket and the database and no list on this
 * console said so — the player lives in the row's detail dialog, so finding a recording meant
 * opening every call in turn and hoping. A recording nobody can find is indistinguishable from
 * one that was never made. Transcripts would have gone the same way, which is why they are here
 * from the start rather than after somebody notices.
 *
 * **One column, up to two icons**, rather than a column each. They answer the same question and
 * a call report is already wide; a second column of mostly-blank cells would cost real width in
 * both tables to say something this one can say by showing a second glyph.
 *
 * Icons and not play buttons, deliberately. Playing needs the dialog anyway (that is where the
 * audio element, the scrubber and the download live), the row already opens it on click, and a
 * second interactive control inside a row that is itself a button is a keyboard trap and a
 * mis-click waiting to happen. The column's job is to say *which rows are worth opening*.
 *
 * The two flags are scoped differently server-side and mean different things. `has_recording` is
 * "yours to hear" — a recording is a row with an owner of its own — so this never advertises
 * audio the reader would be 404ed on. Not `recording_id`: that one only says the switch started
 * a recording, it names a different leg's file on a bridged call, and it is set on calls whose
 * audio never reached the bucket. `has_transcript` is scoped to the CALL, because a transcript
 * has no owner but the conversation, and it is false while a job is still decoding — a queued
 * pass is not something to read.
 */
export function MediaHead() {
  return (
    <TableHead className="w-12">
      <span className="sr-only">Recording and transcript</span>
    </TableHead>
  );
}

export function MediaCell({ call }: { call: Pick<Cdr, "has_recording" | "has_transcript"> }) {
  if (!call.has_recording && !call.has_transcript) {
    // A cell, not nothing: an absent <td> shifts every column after it on that row.
    return <TableCell aria-hidden />;
  }
  return (
    <TableCell>
      <span className="flex items-center gap-1">
        {call.has_recording && (
          <Tooltip content="This call was recorded — open it to listen">
            <Mic className="size-3.5 text-accent-11" aria-label="Recorded" />
          </Tooltip>
        )}
        {call.has_transcript && (
          <Tooltip content="This call was transcribed — open it to read">
            <FileText className="size-3.5 text-success-11" aria-label="Transcribed" />
          </Tooltip>
        )}
      </span>
    </TableCell>
  );
}

/**
 * One totals tile. The value is always rendered from the summary, never from the rows on
 * screen — a tile that counted the visible page would be a report about a scroll position.
 */
export function SummaryTile({
  label, value, hint, loading,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  loading?: boolean;
}) {
  return (
    <Card className="px-4 py-3">
      <p className="text-2xs font-medium uppercase tracking-wide text-text-subtle">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-text">
        {loading ? <span className="text-text-subtle">—</span> : value}
      </p>
      {hint && <p className="mt-0.5 truncate text-xs text-text-muted">{hint}</p>}
    </Card>
  );
}
